import React, { Component } from 'react';
import autobind from 'react-autobind';
import PropTypes from 'prop-types';
import PctySurvey from '@pcty/survey';
import * as JsonConverter from '../../Services/surveyJsConverters';
import isEqual from 'lodash.isequal';
import findIndex from 'lodash/findIndex';
import { replaceAt } from '@pcty/utils';
import ErrorBoundary from '../../../Common/Components/errorBoundary';

import './citrusFormViewer.less'


class CitrusFormViewer extends Component {

  constructor(props) {
    super(props);
    autobind(this);

    const settings = {
      duration: 2,
      questionsInOrder: true,
      oneQuestionAtATime: false,
      displayQuestionsInPages: true,
      backwardMovement: true,
      hideHeader: true,
      hideFooter: true,
      hideComplete: false,
      useInlineActions: true,
      displayPageNumbers: false,
      captions: props.captions,
      allowDecline: props.allowDeclineForm,
      allowCancel: props.allowCancelForm,
      allowSaveDraft: props.allowSaveDraftForm,
      isOptOutButtonDisabled: props.isOptOutButtonDisabled,
    };

    let responses = [];
    let configQuestions = [];

    if (props.configuration) {
      configQuestions = JsonConverter.convertSurveyjsToCitrusJsonConfiguration(props.configuration);

      if (props.answers) {
        responses = JsonConverter.convertSurveyjsJsonAnswersToCitrusResponses(props.answers, configQuestions);
        configQuestions = JsonConverter.applySavedOtherTextToQuestions(configQuestions, responses);
      }
    }

    this.state = {
      settingsDrawerOpen: false,
      settings,
      questions: configQuestions,
      responses
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.configuration, nextProps.configuration)) {
      let configQuestions = [];
      let responses = [];

      if (nextProps.configuration) {
        configQuestions = JsonConverter.convertSurveyjsToCitrusJsonConfiguration(nextProps.configuration);

        if (nextProps.answers) {
          responses = JsonConverter.convertSurveyjsJsonAnswersToCitrusResponses(nextProps.answers, configQuestions);
          configQuestions = JsonConverter.applySavedOtherTextToQuestions(configQuestions, responses);
        }
      }

      this.setState({questions: configQuestions, responses});
    }
  }

  onResponse(id, response, allVisibleQuestionsCount) {
    const index = findIndex(this.state.responses, x => x.id === id);
    // Ticking "Other" after other options keeps them ticked (the Citrus checkbox drops the one ticked last).
    const checkedResponse = JsonConverter.keepSelectionsWhenOtherIsTicked(index < 0 ? null : this.state.responses[index], response);
    const responses = (index < 0) ? [...this.state.responses, checkedResponse] : replaceAt(this.state.responses, index, checkedResponse);

    this.setState({responses});
    const answers = JsonConverter.convertCitrusResponsesSurveyjsJsonAnswers(responses);

    if (this.props.onFormModified) {
      this.props.onFormModified(answers, allVisibleQuestionsCount);
    }
  }

  onComment(id, comment) {
    const index = findIndex(this.state.responses, x => x.id === id);
    const responses = (index < 0) ? [...this.state.responses, comment] : replaceAt(this.state.responses, index, comment);

    this.setState({responses});
    const answers = JsonConverter.convertCitrusResponsesSurveyjsJsonAnswers(responses);

    if (this.props.onFormModified) {
      this.props.onFormModified(answers);
    }
  }

  onCancelForm() {
    if (this.props.allowCancelForm && this.props.onFormCancel) {
      this.props.onFormCancel();
    }
  }

  onDeclineForm() {
    if (this.props.allowDeclineForm && this.props.onFormDecline) {
      this.props.onFormDecline();
    }
  }

  onSaveDraftForm() {
    if (this.props.allowSaveDraftForm && this.props.onFormSaveDraft) {
      this.props.onFormSaveDraft();
    }
  }

  onCompleteForm(questions, responses, allVisibleQuestionsCount) {
    if (this.props.onFormComplete) {
      this.setState({responses});
      const answers = JsonConverter.convertCitrusResponsesSurveyjsJsonAnswers(responses);

      if (this.props.onFormModified) {
        this.props.onFormModified(answers);
      }

      this.props.onFormComplete(allVisibleQuestionsCount);
    }
  }


  componentDidUpdate(prevProps, prevState) {
    this.enableOptOutButtonAfterDraftSaved(prevProps);
  }

  // If the formViewer.saveAsDraftIfDirty is called, the citrusFormViewer component will re-render and re-enable the opt out button.
  // Basically, once we have the data saved in the database, then it makes sense to allow opt out of the open survey
  // otherwise we don't allow opting out from open surveys
  enableOptOutButtonAfterDraftSaved(prevProps) {
    if (this.props.isOptOutButtonDisabled !== prevProps.isOptOutButtonDisabled) {
      this.setState({
        settings: {
          ...this.state.settings,
          isOptOutButtonDisabled: this.props.isOptOutButtonDisabled
        }
      });
    }
  }

  render() {
    let settings = {...this.state.settings};
    settings.hideComplete = (this.props.displayMode !== 'edit');
    settings.displayQuestionsInPages = (this.props.displayMode !== 'display');
    settings.allowDecline = this.props.allowDeclineForm;

    const previewOnly = this.props.displayMode === "display";
    const flattenedQuestions = this.state.questions ? this.state.questions.flat() : [];

    return (<ErrorBoundary>
              <PctySurvey
                key="form-viewer-key"
                id="form-viewer-survey"
                name={this.props.formName}
                questions={flattenedQuestions}
                settings={settings}
                responses={this.state.responses}
                previewOnly={previewOnly}
                onResponse={this.onResponse}
                onComment={this.onComment}
                onCommitResponse={() => {}}
                onBack={() => {}}
                onComplete={(questions, responses, allVisibleQuestionsCount) => this.onCompleteForm(questions, responses, allVisibleQuestionsCount)}
                onDecline={() => this.onDeclineForm()}
                onCancel={() => this.onCancelForm()}
                onSaveDraft={() => this.onSaveDraftForm()}
                onGetViewVideoToken={(videoId) => this.props.onGetViewVideoToken(videoId)}
                videoV2Configuration={this.props.videoV2Configuration}
              />
            </ErrorBoundary>);
  }
}

CitrusFormViewer.propTypes = {
  formName: PropTypes.string,
  configuration: PropTypes.string,
  answers: PropTypes.string,
  locale: PropTypes.string,
  captions: PropTypes.array,
  displayMode: PropTypes.oneOf(['display', 'edit', 'preview']),
  allowSaveDraftForm: PropTypes.bool,
  allowDeclineForm: PropTypes.bool,
  allowCancelForm: PropTypes.bool,
  onFormModified: PropTypes.func,
  onFormSaveDraft: PropTypes.func,
  onFormComplete: PropTypes.func,
  onFormCancel: PropTypes.func,
  onFormDecline: PropTypes.func,
  onGetViewVideoToken: PropTypes.func,
  isOptOutButtonDisabled: PropTypes.bool,
  videoV2Configuration: PropTypes.object.isRequired,
};

CitrusFormViewer.defaultProps = {
  formName: '',
  configuration: null,
  answers: null,
  locale: 'en',
  captions: [],
  displayMode: 'display',
  allowSaveDraftForm: false,
  allowDeclineForm: false,
  allowCancelForm: false,
  isOptOutButtonDisabled: false,
  videoV2Configuration: {
    idpToken: '',
    isVideoV2Enabled: false,
  },
};

export default CitrusFormViewer
